/** Builds the system + user prompts for the completeness assessment agent (Hook 1). */
export function buildCompletenessPrompt(params: {
  submissionPromptText: string
  additionalInfoPromptText: string
  submissionText: string
  priorQAPairs: Array<{ questions: string[]; answer: string }>
  issueCategory?: string
  affectedSystem?: string
  actionRequested?: string
}): { system: string; user: string } {
  const { submissionPromptText, additionalInfoPromptText, submissionText, priorQAPairs, issueCategory, affectedSystem, actionRequested } = params

  const priorContext =
    priorQAPairs.length > 0
      ? '\n\nPrior clarification exchanges:\n' +
        priorQAPairs
          .map(
            (pair, i) =>
              `Round ${i + 1} questions:\n${pair.questions.map((q) => `- ${q}`).join('\n')}\nAnswer: ${pair.answer}`,
          )
          .join('\n\n')
      : ''

  const system = `You are evaluating whether a factory production line issue report contains \
sufficient information for an expert consultant to begin formulating a response.

The line manager was asked: "${submissionPromptText}"
If more info was requested, the prompt used was: "${additionalInfoPromptText}"

Score the submission on a scale of 0.0–1.0 across these dimensions:
- problem_description: Is the problem clearly described?
- affected_equipment: Is the machine/line/component identified?
- observed_symptoms: Are symptoms and behavior described?
- timeline: When did the issue start? Is it intermittent or constant?
- attempted_remedies: Has anything been tried?
- impact: What is the production impact?

Overall score is the mean of dimension scores.
If score < threshold the caller will ask the line manager for more information.
Generate specific, targeted questions that address only the missing dimensions.
Do not repeat questions from prior clarification rounds.

Respond with valid JSON only — no markdown, no prose outside the JSON object.`

  const structuredContext = [
    issueCategory && `Issue category: ${issueCategory}`,
    affectedSystem && `Affected system: ${affectedSystem}`,
    actionRequested && `Action requested: ${actionRequested}`,
  ].filter(Boolean).join('\n')

  const user = `Issue report:
${submissionText}${structuredContext ? '\n\nDeclared structured fields:\n' + structuredContext : ''}${priorContext}

Respond with:
{
  "score": <float 0.0–1.0>,
  "dimension_scores": {
    "problem_description": <float>,
    "affected_equipment": <float>,
    "observed_symptoms": <float>,
    "timeline": <float>,
    "attempted_remedies": <float>,
    "impact": <float>
  },
  "missing_dimensions": [<string>, ...],
  "questions": [<string>, ...]
}`

  return { system, user }
}
